import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  Kbd,
  Loader,
  ScrollArea,
  SegmentedControl,
  Text,
} from '@mantine/core';
import { Spotlight } from '@mantine/spotlight';
import IconX from '@tabler/icons-react/dist/esm/icons/IconX.mjs';
import {
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';

import { hasSearchCommitActions } from '../../model/search-pages';
import type { AppSearchAction, AppSearchCommitMode } from '../../model/types';
import { canDrillAction, canOpenAction } from './action-intent';
import classes from './spotlight-results.module.css';

const LIST_MAX_HEIGHT = 400;
const ICON_SLOT = 22;
const ROW_INDENT = 16;

const HintButton = ({
  kbd,
  label,
  onClick,
}: {
  kbd: string;
  label: string;
  onClick: () => void;
}) => (
  <Button
    component='span'
    variant='subtle'
    size='xs'
    className={classes.hintButton}
    leftSection={
      <Kbd size='xs' className={classes.hintKbd}>
        {kbd}
      </Kbd>
    }
    onMouseDown={(event) => event.preventDefault()}
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
  >
    {label}
  </Button>
);

const ActionHints = ({
  action,
  onDrill,
  onCommit,
}: {
  action: AppSearchAction;
  onDrill: (action: AppSearchAction) => void;
  onCommit: (action: AppSearchAction) => void;
}) => {
  const { t } = useTranslation('header');
  const canDrill = canDrillAction(action);
  const canOpen = canOpenAction(action);

  if (!canDrill && !canOpen) {
    return null;
  }

  return (
    <Group className={classes.hints} gap={6} wrap='nowrap'>
      {canDrill ? (
        <HintButton
          kbd='Tab'
          label={
            action.searchable ? t('search.hint.search') : t('search.hint.drill')
          }
          onClick={() => onDrill(action)}
        />
      ) : null}
      {canOpen ? (
        <HintButton
          kbd='↵'
          label={t('search.hint.open')}
          onClick={() => onCommit(action)}
        />
      ) : null}
    </Group>
  );
};

const ActionRow = ({
  action,
  index,
  onCommit,
  onDrill,
  onHover,
  onRemove,
  removeLabel,
}: {
  action: AppSearchAction & { depth: number };
  index: number;
  onCommit: (action: AppSearchAction) => void;
  onDrill: (action: AppSearchAction) => void;
  onHover?: (index: number, row: HTMLElement) => void;
  onRemove?: (action: AppSearchAction) => void;
  removeLabel?: string;
}) => (
  <Spotlight.Action
    key={action.id}
    className={classes.action}
    classNames={{ actionLabel: classes.actionLabel }}
    data-search-id={action.id}
    onMouseEnter={(event) => onHover?.(index, event.currentTarget)}
    onMouseMove={(event) => onHover?.(index, event.currentTarget)}
    data-alias={action.alias}
    style={
      action.alias
        ? ({
            '--spotlight-alias': `"${action.alias}"`,
          } as CSSProperties)
        : undefined
    }
    label={action.label}
    description={action.description}
    keywords={action.keywords}
    dimmedSections={false}
    leftSection={
      <Box
        w={ICON_SLOT}
        h={ICON_SLOT}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {action.icon}
      </Box>
    }
    highlightQuery
    rightSection={
      <Group gap={4} wrap='nowrap'>
        {onRemove ? (
          <ActionIcon
            component='span'
            className={classes.removeButton}
            variant='subtle'
            color='gray'
            size='sm'
            radius='sm'
            data-testid='app-search-recent-remove'
            aria-label={removeLabel}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove(action);
            }}
          >
            <IconX size={14} />
          </ActionIcon>
        ) : null}
        <ActionHints action={action} onDrill={onDrill} onCommit={onCommit} />
      </Group>
    }
    pl={12 + action.depth * ROW_INDENT}
    closeSpotlightOnTrigger={false}
    onClick={() => onCommit(action)}
  />
);

export const SpotlightResults = ({
  actions,
  recentActions = [],
  listRef,
  scopeKey,
  isLoading,
  commitMode,
  onCommitModeChange,
  onCommit,
  onDrill,
  onHover,
  onRemoveRecent,
  ignoreHover = false,
}: {
  actions: Array<AppSearchAction & { depth: number }>;
  recentActions?: Array<AppSearchAction & { depth: number }>;
  listRef: RefObject<HTMLDivElement | null>;
  scopeKey?: string;
  isLoading: boolean;
  commitMode: AppSearchCommitMode;
  onCommitModeChange: (mode: AppSearchCommitMode) => void;
  onCommit: (action: AppSearchAction) => void;
  onDrill: (action: AppSearchAction) => void;
  onHover?: (index: number, row: HTMLElement) => void;
  onRemoveRecent?: (action: AppSearchAction) => void;
  ignoreHover?: boolean;
}) => {
  const { t } = useTranslation('header');
  const resultsViewportRef = useRef<HTMLDivElement>(null);
  const showCommitModes = hasSearchCommitActions([
    ...recentActions,
    ...actions,
  ]);

  useLayoutEffect(() => {
    if (!scopeKey) {
      return;
    }

    resultsViewportRef.current?.scrollTo({ top: 0 });
  }, [scopeKey]);

  return (
    <>
      {showCommitModes ? (
        <Box px='sm' py={6}>
          <SegmentedControl
            fullWidth
            size='xs'
            value={commitMode}
            onChange={(value) =>
              onCommitModeChange(value as AppSearchCommitMode)
            }
            onMouseDown={(event) => event.preventDefault()}
            onKeyDown={(event) => {
              if (
                event.key === 'ArrowLeft' ||
                event.key === 'ArrowRight' ||
                event.key === 'ArrowUp' ||
                event.key === 'ArrowDown'
              ) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            data={[
              { value: 'both', label: t('search.mode.both') },
              { value: 'select', label: t('search.mode.select') },
              { value: 'search', label: t('search.mode.search') },
            ]}
          />
        </Box>
      ) : null}
      {actions.length === 0 && recentActions.length === 0 && !isLoading ? (
        <Spotlight.Empty>{t('search.nothingFound')}</Spotlight.Empty>
      ) : (
        <Spotlight.ActionsList
          styles={{ actionsList: { maxHeight: 'none', overflow: 'visible' } }}
        >
          <ScrollArea.Autosize
            mah={LIST_MAX_HEIGHT}
            type='always'
            offsetScrollbars='y'
            viewportRef={resultsViewportRef}
          >
            <Box
              ref={listRef}
              className={ignoreHover ? classes.ignoreHover : undefined}
            >
              {recentActions.length > 0 ? (
                <Box className={classes.recentList}>
                  <Divider
                    my='xs'
                    mx='sm'
                    label={t('search.sections.recent')}
                    labelPosition='left'
                  />
                  {recentActions.map((action, index) => (
                    <ActionRow
                      key={`recent:${action.id}`}
                      action={action}
                      index={index}
                      onCommit={onCommit}
                      onDrill={onDrill}
                      onHover={onHover}
                      onRemove={onRemoveRecent}
                      removeLabel={t('search.recent.remove')}
                    />
                  ))}
                </Box>
              ) : null}
              {isLoading && actions.length === 0 ? (
                <Group h={40} gap='xs' px='md' wrap='nowrap'>
                  <Loader size='xs' />
                  <Text size='sm' c='dimmed'>
                    {t('search.loading')}
                  </Text>
                </Group>
              ) : null}
              {actions.length > 0 ? (
                <Box className={classes.resultsList}>
                  <Divider
                    my='xs'
                    mx='sm'
                    label={t('search.sections.results')}
                    labelPosition='left'
                  />
                  {actions.map((action, index) => (
                    <ActionRow
                      key={action.id}
                      action={action}
                      index={recentActions.length + index}
                      onCommit={onCommit}
                      onDrill={onDrill}
                      onHover={onHover}
                    />
                  ))}
                </Box>
              ) : null}
            </Box>
          </ScrollArea.Autosize>
        </Spotlight.ActionsList>
      )}
    </>
  );
};